import React from "react";
import Image from "next/image";
import { toWebp } from "@/utils/common";

interface OverviewProps {
  data?: any;
  image_position?: string;
  isFirst?: boolean;
  isLast?: boolean;
}

const Overview: React.FC<OverviewProps> = ({ data, image_position, isFirst = true, isLast = true }) => {
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

  // Guard: don't render if essential data is missing
  if (!data?.title && !data?.description && !data?.image?.data?.attributes?.url) {
    return null;
  }

  const imageUrl = data?.image?.data?.attributes?.url
    ? toWebp(`${baseUrl}${data.image.data.attributes.url}`)
    : null;

  // When image_position === "start": image LEFT, content RIGHT
  const isImageLeft = image_position === "start";

  const ContentBlock = (
    <div className={`sm:text-left xl:col-span-8 md:col-span-6 ${isImageLeft ? "md:order-2" : "md:order-1"}`}>
      {data?.title && (
        <h2 className="text-heading-3 font-bold text-primary-text mb-[24px]">
          {data.title}
        </h2>
      )}
      {data?.description && (
        <p className="text-primary-text text-subtitle-1 font-regular mb-[24px] mt-[10px]">
          {data.description}
        </p>
      )}
      {data?.["description-1"] && (
        <p className="text-primary-text text-subtitle-1 font-regular mb-[24px] mt-[10px]">
          {data["description-1"]}
        </p>
      )}
    </div>
  );

  const ImageBlock = imageUrl ? (
    <div className={`xl:col-span-4 md:col-span-6 ${isImageLeft ? "md:order-1" : "md:order-2"}`}>
      <div className="img w-full h-full flex items-center justify-center">
        <Image
          src={imageUrl}
          height={390}
          width={455}
          className="w-full h-auto object-cover rounded-lg"
          alt={data?.title ?? ""}
          quality={100}
          priority
          loading="eager"
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      </div>
    </div>
  ) : null;

  // Build padding classes dynamically to avoid double-spacing between stacked cards:
  // - top padding only on the first card in a group
  // - bottom padding only on the last card in a group
  // - mid-gap (≈ half of full py) applied as top padding on non-first cards
  const paddingClass = [
    isFirst ? "pt-[56px] md:pt-[83px] xl:pt-[100px]" : "pt-[28px] md:pt-[41px] xl:pt-[50px]",
    isLast  ? "pb-[56px] md:pb-[83px] xl:pb-[100px]" : "pb-[28px] md:pb-[41px] xl:pb-[50px]",
  ].join(" ");

  return (
    <section className={`relative bg-background-white text-primary-text ${paddingClass} overflow-hidden`}>
      <div className="container mx-auto px-4 text-center relative z-10">
        <div className="flex flex-col items-center justify-center mx-auto">
          <div className="grid md:grid-cols-12 gap-6 md:gap-[30px] items-center w-full">
            {ContentBlock}
            {ImageBlock}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Overview;
